import {
  IAutomationActionTemplateAsanaCreateTask,
  IAutomationTemplate
} from '@dadohr/shared/domainTypes/experience';
import {
  AsanaProject,
  AsanaWorkspace
} from '@dadohr/shared/domainTypes/integrations/asana';
import { TextField } from '@material-ui/core';
import React, { SetStateAction, useMemo } from 'react';
import { AutocompleteSingle } from '../../../../../../../components/Autocomplete';
import { WithShape } from '../../../../../../../components/Charts/Util';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { MarkdownTextField } from '../../../../../../../components/MarkdownText';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { joinWithCommaAndAnd } from '../../../../../../../services/enumerate';
import { ONE_DAY } from '../../../../../../../services/time';
import { toSearchTermRegexp } from '../../../../../../../services/user';
import {
  ASANA_COLOR_CODE_TO_HEX,
  getAsanaProjectsByWorkspaceId,
  getAsanaWorkspaces
} from '../../../../../../Org/pages/Integrations/integrations/asana/service';
import { TaskBuilderContext } from '../../../TaskBuilder/types';
import {
  ActionActorSelectorMulti,
  ActionActorSelectorSingle,
  actionActorToString
} from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  VALIDATORS_ASANA_CREATE_TASK,
  useFieldValidator
} from '../../validation';

const WorkspaceSelector = ({
  orgId,
  value,
  onChange,
  error,
  helperText,
  onBlur
}: {
  orgId: string;
  value: AsanaWorkspace;
  onChange: (nextValue: AsanaWorkspace) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  onBlur?: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const optsFn = useMemo(() => () => getAsanaWorkspaces(orgId), [orgId]);
  return (
    <AutocompleteSingle
      value={value.id ? value : null}
      onChange={(nV) => nV && onChange(nV)}
      options={optsFn}
      getOptionLabel={(o) => o.name}
      error={error}
      helperText={helperText}
      onBlur={onBlur}
      filterOptions={(ws, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return ws.filter((w) => w.name.match(t));
      }}
      disabledClearable
      fullWidth
      label={t('Workspace')}
    />
  );
};

const ProjectSelector = ({
  orgId,
  workspaceId,
  value,
  onChange,
  error,
  helperText,
  onBlur
}: {
  orgId: string;
  workspaceId: string;
  value: AsanaProject;
  onChange: (nextValue: AsanaProject) => void;
  error?: boolean;
  helperText?: React.ReactNode;
  onBlur?: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const optsFn = useMemo(
    () => async () =>
      workspaceId ? getAsanaProjectsByWorkspaceId(orgId, workspaceId) : [],
    [orgId, workspaceId]
  );
  return (
    <AutocompleteSingle
      value={value.id ? value : null}
      onChange={(nV) => nV && onChange(nV)}
      error={error}
      helperText={helperText}
      onBlur={onBlur}
      options={optsFn}
      getOptionLabel={(o) => o.name}
      renderOption={(o) => (
        <WithShape
          shape="circle"
          color={ASANA_COLOR_CODE_TO_HEX[o.color] || ''}
        >
          {o.name}
        </WithShape>
      )}
      filterOptions={(ws, s) => {
        const t = toSearchTermRegexp(s.inputValue);
        return ws.filter((w) => w.name.match(t));
      }}
      disabledClearable
      fullWidth
      label={t('Project')}
    />
  );
};

export const ActionSettingsAsanaCreateTask = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation
}: {
  value: IAutomationActionTemplateAsanaCreateTask;
  onChange: (nextValue: IAutomationActionTemplateAsanaCreateTask) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
}) => {
  const { t } = useUnsafeTranslation();

  const validateTitle = useFieldValidator(
    setValidation,
    VALIDATORS_ASANA_CREATE_TASK,
    'title'
  );
  const invalidTitle = validation?.fields.title || null;

  const validateWorkspace = useFieldValidator(
    setValidation,
    VALIDATORS_ASANA_CREATE_TASK,
    'workspace'
  );
  const invalidWorkspace = validation?.fields.workspace || null;

  const validateProject = useFieldValidator(
    setValidation,
    VALIDATORS_ASANA_CREATE_TASK,
    'project'
  );
  const invalidProject = validation?.fields.project || null;
  return (
    <div>
      <FormRow>
        <WorkspaceSelector
          orgId={expTpl.orgId}
          value={value.workspace}
          onChange={(nextWorkspace) => {
            const nextValue = {
              ...value,
              workspace: nextWorkspace
            };
            invalidWorkspace && validateWorkspace(nextValue, expTpl, aTpl);
            onChange(nextValue);
          }}
          error={!!invalidWorkspace}
          helperText={invalidWorkspace}
          onBlur={() => validateWorkspace(value, expTpl, aTpl)}
        />
      </FormRow>

      <FormRow>
        <ProjectSelector
          orgId={expTpl.orgId}
          workspaceId={value.workspace.id}
          value={value.project}
          onChange={(nextProject) => {
            const nextValue = {
              ...value,
              project: nextProject
            };
            invalidProject && validateProject(nextValue, expTpl, aTpl);
            return onChange(nextValue);
          }}
          error={!!invalidProject}
          helperText={invalidProject}
          onBlur={() => validateProject(value, expTpl, aTpl)}
        />
      </FormRow>

      <FormRow>
        <TextField
          label={t('Task Name')}
          value={value.title}
          error={!!invalidTitle}
          helperText={invalidTitle}
          onBlur={() => validateTitle(value, expTpl, aTpl)}
          onChange={(ev) => {
            const nextValue = {
              ...value,
              title: ev.target.value
            };
            invalidTitle && validateTitle(nextValue, expTpl, aTpl);
            onChange(nextValue);
          }}
          fullWidth
        />
      </FormRow>

      <FormRow>
        <ActionActorSelectorSingle
          label={t('Assignee')}
          value={value.assignee}
          onChange={(nextValue) => onChange({ ...value, assignee: nextValue })}
          expTpl={expTpl}
          fullWidth
        />
      </FormRow>

      <FormRow>
        <TextField
          label={t('Due')}
          value={Math.round(value.duration / ONE_DAY)}
          type="number"
          inputProps={{ min: 0 }}
          onChange={(ev) => {
            const nextValue = +ev.target.value * ONE_DAY;
            onChange({ ...value, duration: nextValue });
          }}
        />
        <div>{t('days after task is created')}</div>
      </FormRow>

      <FormRow>
        <div style={{ width: '100%' }}>
          <MarkdownTextField
            label={t('Notes')}
            value={value.description.t}
            autoFocus
            fullWidth
            rows={4}
            variant="outlined"
            noMarkdownHint
            onChange={(ev) => {
              const nextValue: IAutomationActionTemplateAsanaCreateTask = Object.assign(
                {},
                value,
                {
                  description: Object.assign({}, value.description, {
                    t: ev.target.value
                  })
                }
              );
              onChange(nextValue);
            }}
          />
        </div>
      </FormRow>

      <FormRow>
        <ActionActorSelectorMulti
          label={t('Collaborators')}
          value={value.collaborators}
          onChange={(nextValue) =>
            onChange({ ...value, collaborators: nextValue })
          }
          expTpl={expTpl}
          fullWidth
        />
      </FormRow>
    </div>
  );
};

export const ActionSettingsAsanaCreateTaskReadOnly = ({
  value,
  expTpl
}: {
  value: IAutomationActionTemplateAsanaCreateTask;
  expTpl: TaskBuilderContext;
}) => {
  const { t } = useUnsafeTranslation();
  return (
    <div>
      <FormRow>
        <DataFieldWithLabel label={t('Workspace')}>
          {value.workspace.name}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Project')}>
          {value.project.name}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Task Name')}>
          {value.title}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Assignee')}>
          {value.assignee
            ? actionActorToString(value.assignee, expTpl.supportingActors, t)
            : '-'}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Due')}>
          {Math.round(value.duration / ONE_DAY)}{' '}
          {t('days after task is created')}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Collaborators')}>
          {value.collaborators.length
            ? joinWithCommaAndAnd(
                value.collaborators.map((p) =>
                  actionActorToString(p, expTpl.supportingActors, t)
                ),
                t
              )
            : '-'}
        </DataFieldWithLabel>
      </FormRow>
    </div>
  );
};
